import { useSuspenseQuery } from '@tanstack/react-query';
import { Link, useLocation } from '@tanstack/react-router';
import { PageHeader } from '@wodi/ui';
import { useEffect, useRef, type ReactNode } from 'react';

import { sessionQuery } from '../api/session.ts';
import { screen } from '../layout/screen.tsx';
import frame from '../settings/settings.module.css';
import styles from './settings-nav.module.css';
import { SETTINGS_AREAS, settingsAreasFor } from './settings-overview.tsx';

/** Links between the Settings pages this person may open, starting with the overview. */
export function SettingsNav() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const location = useLocation();
  const ref = useRef<HTMLElement>(null);
  // The row scrolls sideways when the areas don't fit: bring the current one into view,
  // without scrolling the page itself.
  useEffect(() => {
    const nav = ref.current;
    const current = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (nav == null || current == null || nav.scrollWidth <= nav.clientWidth) return;
    nav.scrollLeft = Math.max(0, current.offsetLeft - nav.offsetLeft - 16);
  }, [location.pathname]);
  if (session === null) return null;
  const areas = settingsAreasFor(session);
  if (areas.length === 0) return null;
  return (
    <nav aria-label="Settings" className={styles.nav} ref={ref}>
      <ul className={styles.list}>
        <li>
          <Link
            to="/settings"
            className={styles.link}
            activeOptions={{ exact: true }}
            activeProps={{ 'aria-current': 'page' }}
          >
            Overview
          </Link>
        </li>
        {areas.map((area) => (
          <li key={area.to}>
            <Link to={area.to} className={styles.link} activeProps={{ 'aria-current': 'page' }}>
              {area.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * The frame every Settings page shares: the areas, the page's title and actions, one
 * sentence saying what the page is for, then the page. The sentence is the area's
 * description from the overview unless the page gives its own, or `false` for none (a
 * record page under an area, where the title already says what it is).
 */
export function SettingsPage({
  title,
  actions,
  intro,
  back,
  panel = false,
  children,
}: {
  title: string;
  actions?: ReactNode;
  intro?: string | false;
  /** A link back to the list a record page belongs to, above the title. */
  back?: ReactNode;
  /** Put the page's content on one card, for a page with no sections of its own (ADR 0017). */
  panel?: boolean;
  children: ReactNode;
}) {
  const location = useLocation();
  const area = SETTINGS_AREAS.find((a) => a.to === location.pathname);
  const sentence = intro === false ? undefined : (intro ?? area?.description);
  return (
    <div>
      <SettingsNav />
      <div className={frame.body}>
        {back === undefined ? null : <div className={frame.back}>{back}</div>}
        <PageHeader
          title={title}
          {...(sentence === undefined ? {} : { description: sentence })}
          {...(actions === undefined ? {} : { actions })}
        />
        {panel ? <div className={screen.panel}>{children}</div> : children}
      </div>
    </div>
  );
}
